/**
 * Utility helper functions
 */

/**
 * Conditionally join classNames
 * @param  {...string} classes - Class names to join
 * @returns {string} - Joined class names
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Format a phone number for display
 * @param {string} phone - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhone = (phone) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
};

/**
 * Smooth scroll to an element
 * @param {string} elementId - ID of the element to scroll to
 */
export const scrollToElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Extract YouTube video ID from various YouTube URL formats
 * @param {string} url - YouTube URL (regular, shorts, embed, etc.)
 * @returns {string|null} - Video ID or null if not found
 */
export const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // YouTube Shorts
  const shortsRegex = /youtube\.com\/shorts\/([^"&?/\s]{11})/;
  const shortsMatch = url.match(shortsRegex);
  if (shortsMatch) return shortsMatch[1];
  
  // Regular YouTube URLs
  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const youtubeMatch = url.match(youtubeRegex);
  if (youtubeMatch) return youtubeMatch[1];
  
  return null;
};

/**
 * Get YouTube thumbnail URL from video ID
 * @param {string} videoId - YouTube video ID
 * @param {string} quality - Thumbnail quality: 'maxres', 'hq', 'mq', 'sd', 'default'
 * @returns {string} - Thumbnail URL
 */
export const getYouTubeThumbnail = (videoId, quality = 'hqdefault') => {
  if (!videoId) return '';
  const qualityMap = {
    maxres: 'maxresdefault',
    hq: 'hqdefault',
    mq: 'mqdefault',
    sd: 'sddefault',
    default: 'default',
  };
  const qualitySuffix = qualityMap[quality] || quality;
  return `https://img.youtube.com/vi/${videoId}/${qualitySuffix}.jpg`;
};

/**
 * Get YouTube embed URL from video ID
 * Uses youtube-nocookie.com for better compatibility and privacy
 * @param {string} videoId - YouTube video ID
 * @param {boolean} autoplay - Whether to autoplay
 * @returns {string} - Embed URL
 */
export const getYouTubeEmbedUrl = (videoId, autoplay = true) => {
  if (!videoId) return '';
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    enablejsapi: '1',
    origin: window.location.origin,
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
};

/**
 * Get direct YouTube Shorts URL for opening in browser/app
 * @param {string} videoId - YouTube video ID
 * @returns {string} - Direct Shorts URL
 */
export const getYouTubeShortsUrl = (videoId) => {
  if (!videoId) return '';
  return `https://www.youtube.com/shorts/${videoId}`;
};

/**
 * Fetch YouTube video title using oEmbed API
 * @param {string} url - YouTube video URL
 * @returns {Promise<string>} - Video title or fallback
 */
export const fetchYouTubeTitle = async (url) => {
  try {
    const response = await fetch(
      `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`
    );
    if (response.ok) {
      const data = await response.json();
      return data.title || 'Untitled Video';
    }
  } catch (error) {
    console.warn('Failed to fetch YouTube title:', error);
  }
  return 'Untitled Video';
};

/**
 * Extract Google Drive file ID from various URL formats
 * @param {string} url - Google Drive URL
 * @returns {string|null} - File ID or null if not found
 */
export const getGoogleDriveFileId = (url) => {
  if (!url) return null;
  
  // Format: /file/d/{fileId}/view
  const fileRegex = /\/file\/d\/([a-zA-Z0-9_-]+)/;
  const fileMatch = url.match(fileRegex);
  if (fileMatch) return fileMatch[1];
  
  // Format: ?id={fileId}
  const idRegex = /[?&]id=([a-zA-Z0-9_-]+)/;
  const idMatch = url.match(idRegex);
  if (idMatch) return idMatch[1];
  
  return null;
};

/**
 * Get direct image URL from Google Drive file ID
 * @param {string} fileId - Google Drive file ID
 * @returns {string} - Direct image URL
 */
export const getGoogleDriveImageUrl = (fileId) => {
  if (!fileId) return '';
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};

/**
 * Get high-res Google Drive image URL from file ID
 * @param {string} fileId - Google Drive file ID
 * @returns {string} - High-res image URL
 */
export const getGoogleDriveHighResUrl = (fileId) => {
  if (!fileId) return '';
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
};

/**
 * Get Google Drive direct download URL from file ID
 * @param {string} fileId - Google Drive file ID
 * @returns {string} - Direct download URL
 */
export const getGoogleDriveDownloadUrl = (fileId) => {
  if (!fileId) return '';
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};
